import React, { useState } from 'react'
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import * as XLSX from 'xlsx'
import { subDays } from 'date-fns'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { FormProvider, RHFDateRangePickerField } from '../../common/hook-form'
import { MerchantFilterCached } from '../../common/ProshipFormComponents'
import { DownloadWithPolicy } from '../../common/DownloadPolicyDialog'
import { apiConstants, postGeneric } from '../../common/constants'
import { formatDateForApi, parseDateAndTime } from '../../helpers/UtilityHelper'
import { formatDate } from '../../common/util'

// Sequential small pages so no single request carries a huge payload — one giant
// limit-everything call is what times out / hammers the API on big ranges.
const CHUNK_SIZE = 500
// Hard ceiling protecting both the API and the browser's memory while building the
// xlsx. If hit, the file is still produced and the user is told it was truncated.
const MAX_ROWS = 50000

/*
 * Full NDR export dialog (Open / Closed NDR pages).
 * Unlike the old Export CSV (current grid page only), this downloads EVERY record
 * matching the tab — optionally scoped to one merchant and/or an AWB-registered
 * date range — fetching page-by-page so the API is never hit with one huge request.
 */
export default function NdrDownloadDialog({ open, onClose, baseFilter = [], excludes = '', filePrefix = 'NDR', reportName = 'NDR Export' }) {
    const { enqueueSnackbar } = useSnackbar()
    const [allDates, setAllDates] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [fetched, setFetched] = useState(0)

    const methods = useForm({
        defaultValues: {
            merchant: '',
            fromDate: subDays(new Date(), 29),
            toDate: new Date(),
        },
    })
    const { getValues, watch } = methods
    const fromDate = watch('fromDate')
    const toDate = watch('toDate')

    const runDownload = async () => {
        const { merchant, fromDate: from, toDate: to } = getValues()
        setDownloading(true)
        setFetched(0)
        try {
            const andfilter = [...baseFilter]
            if (merchant) {
                andfilter.push({ field: 'merchant.$id', operator: 'eq', value: "ObjectId('" + merchant + "')" })
            }
            if (!allDates && from && to) {
                andfilter.push({ field: 'awbRegisteredDate', operator: 'gte', value: formatDateForApi(from, 'start') })
                andfilter.push({ field: 'awbRegisteredDate', operator: 'lte', value: formatDateForApi(to, 'end') })
            }

            const all = []
            let truncated = false
            for (let offset = 0; ; offset += CHUNK_SIZE) {
                const res = await postGeneric(apiConstants.ORDER_SEARCH, {
                    andfilter,
                    orfilter: [],
                    offset,
                    limit: CHUNK_SIZE,
                    skipCount: true,
                    controllerFilter: true,
                    excludes,
                    // Stable order so offset pagination never skips/duplicates rows between pages.
                    sort: { field: 'awbRegisteredDate', direction: 'DESC' },
                })
                const rows = res?.data?.data || []
                all.push(...rows)
                setFetched(all.length)
                if (rows.length < CHUNK_SIZE) break
                if (all.length >= MAX_ROWS) { truncated = true; break }
            }

            if (!all.length) {
                enqueueSnackbar('No records to download for the selected criteria', { variant: 'info' })
                return
            }

            const out = all.map((r) => ({
                'AWB Number': r.awb_number || '',
                'Merchant': r.merchantDetail?.name || '',
                'Order / Reference': r.reference || '',
                'AWB Registered Date': r.awbRegisteredDate ? parseDateAndTime(r.awbRegisteredDate) : '',
                'Courier': r.courierDetail?.name || '',
                'Order Status': (r.orderStatus || '').replace(/_/g, ' '),
                'NDR Status': r.ndrData?.ndrStatus || '',
                'NDR Reason': r.ndrData?.ndrCpStatusDescription || '',
                'NDR Reported Date': r.ndrData?.ndrReportedDate ? parseDateAndTime(r.ndrData.ndrReportedDate) : '',
                'NDR Comments': r.ndrData?.remark || '',
                'NDR Age (days)': r.ndrData?.ndrAge ?? '',
                'Reattempt Count': r.ndrData?.reAttemptCount ?? '',
                'OFD Count': r.ndrData?.ofdCount ?? '',
                'Current Action': r.ndrData?.currentNdrAction || '',
                'Payment Mode': r.payment_mode || '',
                'Invoice Value': r.invoice_value || '',
                'Customer': r.delivery_details?.to_name || '',
                'City': r.delivery_details?.to_city || '',
                'State': r.delivery_details?.to_state || '',
                'Pincode': r.delivery_details?.to_pincode || '',
            }))
            const ws = XLSX.utils.json_to_sheet(out)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'NDR')
            XLSX.writeFile(wb, `${filePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`)

            if (truncated) {
                enqueueSnackbar(`Downloaded the first ${all.length.toLocaleString()} records — narrow the date range or pick a merchant to get the rest`, { variant: 'warning' })
            } else {
                enqueueSnackbar(`Downloaded ${all.length.toLocaleString()} record(s)`, { variant: 'success' })
            }
            onClose()
        } catch (e) {
            enqueueSnackbar('Download failed, please try again', { variant: 'error' })
        } finally {
            setDownloading(false)
        }
    }

    return (
        <Dialog open={open} onClose={downloading ? undefined : onClose} maxWidth='xs' fullWidth>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Download NDR Report</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
                    Exports every record matching this tab — not just the rows on screen. Optionally
                    scope it to one merchant, and pick an AWB-registered date range or the complete history.
                </Typography>
                <FormProvider methods={methods}>
                    <Box sx={{ mb: 2 }}>
                        <MerchantFilterCached fullWidth placeholder='All merchants' />
                    </Box>
                    <Box sx={{ opacity: allDates ? 0.5 : 1, pointerEvents: allDates ? 'none' : 'auto' }}>
                        <RHFDateRangePickerField label='AWB Registered Date' maxDate={new Date()} />
                    </Box>
                </FormProvider>
                <FormControlLabel
                    sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                    control={<Checkbox size='small' checked={allDates} onChange={(e) => setAllDates(e.target.checked)} />}
                    label='Download complete history (ignore date range)'
                />
                {downloading && (
                    <Typography sx={{ fontSize: 12, color: 'primary.main', mt: 1 }}>
                        Fetched {fetched.toLocaleString()} records…
                    </Typography>
                )}
                {!downloading && !allDates && fromDate && toDate && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1 }}>
                        {formatDate(fromDate)} — {formatDate(toDate)}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={downloading}>Cancel</Button>
                <DownloadWithPolicy onConfirm={runDownload} reportName={reportName}>
                    <LoadingButton
                        loading={downloading}
                        variant='contained'
                        startIcon={<FileDownloadOutlinedIcon />}
                    >
                        Download
                    </LoadingButton>
                </DownloadWithPolicy>
            </DialogActions>
        </Dialog>
    )
}
